/*!
 * Copyright Optimus Ride 2020
 */
import firebase from "firebase";
import jwt_decode from 'jwt-decode';
import { APPLICATION_NAME } from '@optimusride/client-library/constants';
import UserServiceConsumer from '../api/user';

interface firebaseDecodedJwt {
    commandor_role: string;
}

export default abstract class Auth {
    private app: APPLICATION_NAME;
    private token?: string;
    private hostname: string;

    constructor(app: APPLICATION_NAME, hostname?: string, token?: string) {
        if (hostname === undefined) {
            throw new Error('hostname is required.');
        }

        this.app = app;
        this.token = token;
        this.hostname = hostname;
    }

    public abstract login(email: string, password: string): Promise<firebase.auth.UserCredential>;

    public abstract loginWithGSuite(): Promise<firebase.auth.UserCredential>;

    public abstract sendPhoneCode(phoneNumber: string): Promise<firebase.auth.ConfirmationResult>;

    public abstract loginWithPhone(confirmationResult: firebase.auth.ConfirmationResult, code: string): Promise<firebase.auth.UserCredential>;

    public abstract sendEmailLink(email: string): Promise<void>;

    public abstract loginWithEmailLink(link: string, email: string): Promise<firebase.auth.UserCredential>;

    public abstract onAuthStateChanged(handler: (user: firebase.User | null) => void): void;

    public abstract logout(path?: string): Promise<void>;

    public abstract resetUserPassword(email: string): Promise<boolean>;

    public abstract updateUserPassword(newPassword: string): Promise<boolean>;

    public getIdToken = async () => {
        if (this.token) {
            return this.token;
        }

        const user = this.currentUser();

        if (user) {
            return await user.getIdToken(true);
        } else {
            return null;
        }
    }

    public isCommandorAuthorized = async (roles: (string | null)[]) => {
        const token = await this.getIdToken();

        if (token) {
            const decoded: firebaseDecodedJwt = jwt_decode(token);
            const commandor_role = decoded.commandor_role;
            if (commandor_role) {
                return roles.includes(commandor_role.toUpperCase());
            } else {
                return false;
            }
        } else {
            return false;
        }
    }

    public abstract currentUser(): firebase.User | null;

    protected isAuthorized = async () => {
        // creating a new instance of UserServiceConsumer rather than using the globally defined one
        // to avoid a circular dependency with Auth and UserServiceConsumer
        const userServiceConsumer = new UserServiceConsumer(this, this.hostname);
        const userType = await userServiceConsumer.getUserType();
        switch (this.app) {
            case APPLICATION_NAME.COMMANDOR:
                return userType.getCommandor();
            case APPLICATION_NAME.ORIDE:
                return userType.getOride();
            case APPLICATION_NAME.SYNC_STATION:
                return userType.getSyncstation();
            case APPLICATION_NAME.COMMAND:
                return userType.getCommand();
            default:
                return false;
        }
    }

    protected emptyError = () => {
        return { code: null, message: null };
    }
}
